import { Navigate, createBrowserRouter } from "react-router";
import { EmptyState } from "@how/ui";
import { RequirePlatformRole, RequireSession } from "../features/auth/guards";
import { RequireMfa, RouteErrorPage, SecurityPage, SignInPage } from "@how/app-kit";
import { env } from "../lib/env";
import { ErrorsPage } from "../features/errors/ErrorsPage";
import { OverviewPage } from "../features/tenants/OverviewPage";
import { SupportSessionsPage } from "../features/tenants/SupportSessionsPage";
import { CreateChurchPage } from "../features/tenants/CreateChurchPage";
import { TenantDetailPage } from "../features/tenants/TenantDetailPage";
import { TenantsPage } from "../features/tenants/TenantsPage";
import { WebhookEventsPage } from "../features/webhooks/WebhookEventsPage";
import { PlatformSettingsPage } from "../features/settings/PlatformSettingsPage";
import { DomainsPage } from "../features/domains/DomainsPage";
import { PlansPage } from "../features/plans/PlansPage";
import { HelpFeedbackPage } from "../features/help-feedback/HelpFeedbackPage";
import { Shell } from "./Shell";

export function createAppRouter() {
  return createBrowserRouter([
    {
      errorElement: <RouteErrorPage />,
      path: "/sign-in",
      element: (
        <SignInPage
          title="HoW Control Plane"
          subtitle="DCX Logic staff sign-in"
          footnote="Access is limited to DCX Logic staff. Two-step verification is required."
        />
      ),
    },
    {
      errorElement: <RouteErrorPage />,
      path: "/",
      element: (
        <RequireSession>
          <RequireMfa required={env.VITE_HOW_REQUIRE_PLATFORM_MFA}>
            <RequirePlatformRole>
              <Shell />
            </RequirePlatformRole>
          </RequireMfa>
        </RequireSession>
      ),
      children: [
        { index: true, element: <OverviewPage /> },
        // Aurora, the same Overview in the night-and-glass design (preview, restored 2026-10-13). The old /home2/1 to
        // /home2/4 addresses open it too.
        { path: "home2", element: <OverviewPage design="aurora" /> },
        { path: "home2/*", element: <Navigate to="/home2" replace /> },
        { path: "tenants", element: <TenantsPage /> },
        // A super admin's page (ADR 0064); the page says so to anyone else, and the database refuses them.
        { path: "tenants/new", element: <CreateChurchPage /> },
        { path: "tenants/:tenantId", element: <TenantDetailPage /> },
        { path: "support", element: <SupportSessionsPage /> },
        { path: "errors", element: <ErrorsPage /> },
        { path: "webhooks", element: <WebhookEventsPage /> },
        { path: "help-feedback", element: <HelpFeedbackPage /> },
        // The gallery carries the chart, and with it Recharts (#67): loaded when opened, not with every screen.
        {
          path: "theme",
          lazy: async () => ({
            Component: (await import("../features/theme/ThemePage")).ThemePage,
          }),
        },
        { path: "security", element: <SecurityPage /> },
        { path: "settings", element: <PlatformSettingsPage /> },
        { path: "domains", element: <DomainsPage /> },
        { path: "plans", element: <PlansPage /> },
        {
          path: "*",
          element: (
            <EmptyState
              title="Page not found"
              description="Check the address or use the navigation."
            />
          ),
        },
      ],
    },
  ]);
}
